import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore, useAuthActions } from '@/stores/authStore';

// 页面组件 (懒加载)
const HomePage = React.lazy(() => import('@/pages/Home'));
const LoginPage = React.lazy(() => import('@/pages/Login'));
const RegisterPage = React.lazy(() => import('@/pages/Register'));
const TestPage = React.lazy(() => import('@/pages/Test'));
const ResultPage = React.lazy(() => import('@/pages/Result'));
const HistoryPage = React.lazy(() => import('@/pages/History'));
const LeaderboardPage = React.lazy(() => import('@/pages/Leaderboard'));
const ProfilePage = React.lazy(() => import('@/pages/Profile'));

// 免费测试页面（公开访问）
const FreeTestPage = React.lazy(() => import('@/pages/FreeTest'));
const FreeResultPage = React.lazy(() => import('@/pages/FreeResult'));
const FreeResultEntry = React.lazy(() => import('@/pages/FreeResult/Entry'));
const FinalPage = React.lazy(() => import('@/pages/Final'));
const FinalEntry = React.lazy(() => import('@/pages/Final/Entry'));
const ShareResultPage = React.lazy(() => import('@/pages/ShareResult'));

// 布局组件
const Layout = React.lazy(() => import('@/components/layout/Layout'));

// 加载组件
const PageLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <Spin size="large" />
      <p className="mt-4 text-gray-600">页面加载中...</p>
    </div>
  </div>
);

// 路由守卫组件
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/free" replace />;
  }
  
  return <>{children}</>;
};

const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/free" replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const { initializeAuth } = useAuthActions();
  const location = useLocation();
  const isPublicTestSurface = ['/free', '/final', '/share'].some((path) => location.pathname.startsWith(path));

  // 初始化认证状态
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // 应用初始化加载
  if (isLoading && !isPublicTestSurface) {
    return <PageLoading />;
  }

  return (
    <div className="App">
      <React.Suspense fallback={<PageLoading />}>
        <Routes>
          {/* 公共路由 */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />

          {/* 免费测试路由（公开访问，无需登录） */}
          <Route path="/free" element={<FreeTestPage />} />
          <Route path="/final" element={<FinalEntry />} />
          <Route path="/final/:sessionId" element={<FinalPage />} />
          <Route path="/free/result" element={<FreeResultEntry />} />
          <Route path="/free/result/:sessionId" element={<FreeResultPage />} />
          <Route path="/share/:sessionId" element={<ShareResultPage />} />

          {/* 访问根域名时直接进入免费测试 */}
          <Route path="/" element={<Navigate to="/free" replace />} />

          {/* 受保护的路由 */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="test" element={<TestPage />} />
            <Route path="result/:sessionId" element={<ResultPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* 未知路径统一回到免费测试 */}
          <Route path="*" element={<Navigate to="/free" replace />} />
        </Routes>
      </React.Suspense>
    </div>
  );
};

export default App;
